import { ErrorBoundary } from 'react-error-boundary'
import { Link } from 'react-router'
import { MembershipIcon } from 'src/components/Membership/MembershipIcon'
import { HolidaysEventMembershipTopBanner } from 'src/features/GrowthEvent/Holidays2025/HolidaysEventBanner'
import { useHolidaysEventBannerEnabled } from 'src/features/GrowthEvent/Holidays2025/utils/holidaysEventState'
import { useMyMembership } from 'src/services/apollo/membership'
import { cx } from 'src/utils/cx'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { startEventGroup, track } from 'src/utils/tracker'
import MdiChevronRight from '~icons/mdi/chevron-right'
import Text from '../../../components/ui/Text'
import {
  getMembershipExpireTime,
  shouldShowMembershipCountdown,
} from '../utils/expireStateUtils'
import { MembershipCountdown } from './MembershipCountdown'
import styles from './MembershipHeaderBanner.module.css'

/**
 * Shown when growth events are ongoing
 */
export function OrdinaryMembershipHeaderBanner(props: StyleProps) {
  const isDesktopLayout = useBreakpoint('mlg', 'up')

  const { data: membership } = useMyMembership()
  const expireAt = getMembershipExpireTime(membership?.subscription?.endAt)

  // - ExpireAt is defined (not null or undefined)
  // - Countdown should be shown if expireAt is within 3 days
  const shouldShowCountdown =
    expireAt !== undefined && shouldShowMembershipCountdown(expireAt, 3)

  const renderCountdown = () => {
    return (
      shouldShowCountdown && (
        <MembershipCountdown expireAt={expireAt} className="ml-2" />
      )
    )
  }

  const textLength = isDesktopLayout ? 'long' : 'short'
  const countdownContext = shouldShowCountdown
    ? `countdown_${textLength}`
    : textLength
  const i18nContext = countdownContext

  // credit-packs for members, plans for non-members
  const defaultLinkTo = membership?.subscription?.tier
    ? '/membership/credit-packs'
    : '/membership/plans'

  const linkTo = defaultLinkTo
  return (
    <div
      className={cx(
        '[--height:32px]',
        'relative z-10 grid h-[--height]',
        props.className,
      )}
      style={props.style}
    >
      <div className={cx(styles['bg'], 'row-start-1 col-start-1')} />
      <Link
        to={linkTo}
        className={cx(
          'row-start-1 col-start-1 relative flex justify-center items-center hover:bg-black/10 no-underline transition',
          'text-white font-medium text-shadow shadow-black/50',
        )}
        onClick={() => {
          const trackingLocation = 'top-bar'
          track('enter_member_click', { location: trackingLocation })
          startEventGroup('member_source', trackingLocation, {
            persistent: true,
          })
        }}
      >
        <MembershipIcon
          className={cx('w-5 h-5 mr-2 -translate-y-px drop-shadow')}
        />
        <span className="leading-[100%] text-center lg:text-left">
          <Text
            i18nKey={`membership:membership.banner.label`}
            context={i18nContext}
            components={{ br: <br />, small: <span className="text-xs" /> }}
          />
        </span>
        {renderCountdown()}
        <MdiChevronRight className="w-6 h-6 drop-shadow" />
      </Link>
    </div>
  )
}

export function MembershipHeaderBannerContent(props: StyleProps) {
  const enableHolidaysEventBanner = useHolidaysEventBannerEnabled()
  if (enableHolidaysEventBanner)
    return <HolidaysEventMembershipTopBanner {...props} />

  return <OrdinaryMembershipHeaderBanner {...props} />
}

export default function MembershipHeaderBanner(props: StyleProps) {
  return (
    <ErrorBoundary fallback={<div />}>
      <MembershipHeaderBannerContent {...props} />
    </ErrorBoundary>
  )
}
